/**
 * Org Settings → Recovery. Lists soft-deleted workspaces still in
 * their 30-day recovery window, scoped to the active org (#547 PR 3).
 *
 * Recovery scope per the GitHub-style separation:
 *   - This tab: soft-deleted **workspaces** in the **active org** only.
 *     An org admin cleaning up their tenant lives here.
 *   - Account → Recovery (future): soft-deleted **orgs** the caller
 *     owns. Those cross tenants and don't belong under any single
 *     org's settings.
 *
 * Once the 30-day window expires the cron sweep
 * (`infrastructure/scheduling/hardDeleteSweep`) permanently removes
 * the workspace and every scoped doc under it; until then a single
 * "Restore" click flips `deletedAt` back to unset and the workspace
 * reappears in the Workspaces tab.
 *
 * Gates:
 *   - List: the backend's `GET /api/recover` returns only items the
 *     caller can restore (owner-only for the org-recovery rule); UI
 *     just renders what comes back.
 *   - Restore: same gate the backend enforces — if the API returns
 *     a workspace here, the user can restore it.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import {
  recoveryAPI,
  type OrgWithRole,
  type RecoverableWorkspace,
} from '@/lib/api'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate } from '@/lib/utils'

export function OrgRecovery({ org }: { org: OrgWithRole }) {
  const tz = useUserTimezone()
  const [workspaces, setWorkspaces] = useState<RecoverableWorkspace[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setLoadError(null)
      const result = await recoveryAPI.list()
      // GitHub-style scoping: this tab shows workspaces under THIS
      // org only. Soft-deleted orgs surface in Account → Recovery
      // (future). Filtering client-side keeps the existing /recover
      // endpoint and its tests untouched.
      setWorkspaces(
        result.workspaces
          .filter((w) => w.orgId === org._id)
          .sort((a, b) => a.deletedAt.localeCompare(b.deletedAt)),
      )
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err))
    }
  }, [org._id])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function handleRestore(ws: RecoverableWorkspace) {
    setActionError(null)
    setRestoringId(ws._id)
    try {
      await recoveryAPI.recoverWorkspace(ws._id)
      await refresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to restore workspace')
    } finally {
      setRestoringId(null)
    }
  }

  if (loadError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive" role="alert">
          Failed to load recovery list: {loadError}
        </p>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Recovery</h3>
        <p className="text-sm text-muted-foreground">
          Soft-deleted workspaces under <strong>{org.name}</strong>. Each is
          permanently removed when its 30-day window expires; restore now
          to bring it (and all its data) back. Soft-deleted organizations
          live under your account settings.
        </p>
      </div>

      {actionError && (
        <p className="text-sm text-destructive" role="alert">
          {actionError}
        </p>
      )}

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Deleted</TableHead>
              <TableHead>Time remaining</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workspaces.map((ws) => (
              <TableRow key={ws._id}>
                <TableCell className="font-medium">{ws.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(ws.deletedAt, tz)}
                </TableCell>
                <TableCell className="text-sm">
                  {formatTimeRemaining(ws.recoverableUntil)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleRestore(ws)}
                    disabled={restoringId !== null}
                  >
                    {restoringId === ws._id ? 'Restoring…' : 'Restore'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {workspaces.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">
                  No deleted workspaces in {org.name}.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

/** Render the remaining recovery window as a friendly relative time
 * ("23 days", "4 hours", "less than 1 hour", "expired"). Day-level
 * granularity above 24h, hour-level below — matches GitHub's deleted-
 * repo recovery copy. */
function formatTimeRemaining(recoverableUntil: number): string {
  const ms = recoverableUntil - Date.now()
  if (ms <= 0) return 'expired'
  const hours = Math.floor(ms / (1000 * 60 * 60))
  if (hours >= 24) {
    const days = Math.floor(hours / 24)
    return `${days} day${days === 1 ? '' : 's'}`
  }
  if (hours >= 1) {
    return `${hours} hour${hours === 1 ? '' : 's'}`
  }
  return 'less than 1 hour'
}
